import { useAuth } from 'oidc-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './App.css';
import AppContext from './AppContext';
import CompanyHeader from './components/CompanyHeader/CompanyHeader.tsx';
import Footer from './components/Footer/Footer.tsx';
import Header from './components/Header/Header.tsx';
import ReportOptions from './components/ReportOptions/ReportOptions.tsx';
import ReportContent from './components/ReportContent/ReportContent.tsx';
import Navigation from './components/Navigation/Navigation';
import { DEFAULT_QUARTERLY_VIEW_MODE, QUARTERLY_VIEW_MODE_OPTIONS, DEFAULT_YEARS_MODE, YEARS_MODE_OPTIONS } from './components/model/ViewMode';

function App() {
  const auth = useAuth();
  const cocode = sessionStorage.getItem('cocode');
  const year = sessionStorage.getItem('year');
  const [periodType, setPeriodType] = useState(sessionStorage.getItem('periodType') || null);
  const [quarterlyViewMode, setQuarterlyViewMode] = useState(() => {
    if (!periodType) return null;
    const stored = sessionStorage.getItem('viewMode');
    const validValues = QUARTERLY_VIEW_MODE_OPTIONS.map((opt) => opt.value);
    return validValues.includes(stored) ? stored : DEFAULT_QUARTERLY_VIEW_MODE;
  });
  const [yearsMode, setYearsMode] = useState(() => {
    const stored = sessionStorage.getItem('yearsMode');
    const validValues = YEARS_MODE_OPTIONS.map((opt) => opt.value);
    return validValues.includes(stored) ? stored : DEFAULT_YEARS_MODE;
  });
  // Lags behind yearsMode until every in-flight refresh completes. Consumed by
  // rendering code (grid column count, header count) so column layout stays in
  // sync with the data currently on screen, not the mode the user just clicked.
  const [activeYearsMode, setActiveYearsMode] = useState(yearsMode);
  const [pendingRefreshCount, setPendingRefreshCount] = useState(0);
  const beginRefresh = useCallback(() => {
    setPendingRefreshCount((count) => count + 1);
  }, []);
  const endRefresh = useCallback(() => {
    setPendingRefreshCount((count) => (count > 0 ? count - 1 : 0));
  }, []);
  const isRefreshing = pendingRefreshCount > 0;
  // Only sync activeYearsMode after a real refresh cycle completes. Watching
  // `!isRefreshing && activeYearsMode !== yearsMode` alone is not enough: on
  // the render right after the toggle, no fetch has fired yet, so
  // isRefreshing is still false and activeYearsMode would sync eagerly —
  // widening the grid before the new data arrives. Instead we require the
  // isRefreshing flag to have been true at some point since the last sync,
  // i.e. transition from true → false. useLayoutEffect keeps the sync + the
  // data commit in the same paint, so no phantom-column frame appears.
  const wasRefreshingRef = useRef(false);
  useLayoutEffect(() => {
    if (wasRefreshingRef.current && !isRefreshing && activeYearsMode !== yearsMode) {
      setActiveYearsMode(yearsMode);
    }
    wasRefreshingRef.current = isRefreshing;
  }, [isRefreshing, yearsMode, activeYearsMode]);
  const [showBenchmark, setShowBenchmark] = useState(false);
  const [showIndustryMean, setShowIndustryMean] = useState(false);
  const [riskRepository, setRiskRepository] = useState('FS');
  const [entityId, setEntityId] = useState(null);
  const [statementType, setStatementType] = useState(null);
  const [navIsOpen, setNavIsOpen] = useState(false);
  const [securityCIK, setSecurityCIK] = useState(null);
  const [groupCode, setGroupCode] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('viewMode') === 'preview' ? 'preview' : 'published';
  });

  // Update session storage when tokens are refreshed
  useEffect(() => {
    if (auth.userData?.access_token) {
      sessionStorage.setItem('bearerToken', `Bearer ${auth.userData.access_token}`);
    }
  }, [auth.userData?.access_token]);

  const contextProps = {
    cocode: cocode,
    year: year,
    periodType,
    setPeriodType,
    quarterlyViewMode,
    setQuarterlyViewMode,
    yearsMode,
    setYearsMode,
    activeYearsMode,
    isRefreshing,
    beginRefresh,
    endRefresh,
    showBenchmark,
    setShowBenchmark,
    showIndustryMean,
    setShowIndustryMean,
    riskRepository,
    setRiskRepository,
    entityId,
    setEntityId,
    statementType,
    setStatementType,
    navIsOpen,
    setNavIsOpen,
    securityCIK,
    setSecurityCIK,
    groupCode,
    setGroupCode,
    viewMode,
    setViewMode
  };

  if (!auth.isLoading) {
    return (
      <AppContext.Provider value={contextProps}>
        <div className="App">
          <div className="app_container">
            {navIsOpen && <Navigation />}
            <div className="report_container">
              <Header />
              {viewMode === 'preview' && (
                <div style={{
                  backgroundColor: '#f9a825',
                  color: '#000',
                  textAlign: 'center',
                  padding: '0.4rem',
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  FPR Draft View
                </div>
              )}
              <div className="sticky_header">
                <CompanyHeader />
                <ReportOptions />
              </div>

              <div>
                <ReportContent />
              </div>

              <Footer />
            </div>
          </div>
        </div>
      </AppContext.Provider>
    );
  } else {
    return null;
  }
}

export default App;
